import { User } from "@/hooks/useAuthStore";
import { shortenAddress } from "@/lib/utils";

/**
 * Patterns to identify sensitive data
 */
const SENSITIVE_PATTERNS = {
  // Authentication tokens
  JWT_TOKEN: /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/,
  BEARER_TOKEN: /^Bearer\s+.+$/i,

  // API Keys
  API_KEY: /^[A-Za-z0-9]{32,}$/,

  // Solana wallet addresses (base58, 32-44 chars)
  WALLET_ADDRESS: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,

  // Email addresses
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

/**
 * Sensitive header names to scrub
 */
const SENSITIVE_HEADERS = [
  'authorization',
  'x-auth-token',
  'x-api-key',
  'cookie',
  'x-csrf-token',
  'x-session-id',
];

/**
 * Masks a wallet address using the shortenAddress utility
 */
export function maskWalletAddress(address: string): string {
  if (!address || typeof address !== 'string') return address;

  if (SENSITIVE_PATTERNS.WALLET_ADDRESS.test(address)) {
    return shortenAddress(address, 4);
  }

  return address;
}

/**
 * Sanitizes a value by detecting and masking sensitive data
 */
export function sanitizeValue(value: unknown): unknown {
  if (typeof value === 'string') {
    // Check for JWT tokens
    if (SENSITIVE_PATTERNS.JWT_TOKEN.test(value)) {
      return '[REDACTED_JWT_TOKEN]';
    }

    // Check for Bearer tokens
    if (SENSITIVE_PATTERNS.BEARER_TOKEN.test(value)) {
      return '[REDACTED_BEARER_TOKEN]';
    }

    // Check for API keys
    if (SENSITIVE_PATTERNS.API_KEY.test(value)) {
      return '[REDACTED_API_KEY]';
    }

    // Check for wallet addresses
    if (SENSITIVE_PATTERNS.WALLET_ADDRESS.test(value)) {
      return maskWalletAddress(value);
    }

    // Check for email addresses
    if (SENSITIVE_PATTERNS.EMAIL.test(value)) {
      return '[REDACTED_EMAIL]';
    }
  }

  return value;
}

/**
 * Sanitizes HTTP headers by removing sensitive values
 */
export function sanitizeHeaders(headers: Record<string, unknown>): Record<string, unknown> {
  if (!headers || typeof headers !== 'object') return headers;

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(headers)) {
    const lowerKey = key.toLowerCase();

    if (SENSITIVE_HEADERS.includes(lowerKey)) {
      sanitized[key] = '[REDACTED]';
    } else {
      sanitized[key] = sanitizeValue(value);
    }
  }

  return sanitized;
}

/**
 * Sanitizes user data before sending to Sentry
 * Includes username but masks email and wallet address
 */
export function sanitizeUserData(user: User): Record<string, unknown> {
  if (!user || typeof user !== 'object') return {};

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(user)) {
    switch (key) {
      case 'username':
        // Include username (non-sensitive)
        sanitized[key] = value;
        break;

      case 'name':
        // Redact name
        sanitized[key] = '[REDACTED_NAME]';
        break;

      case 'id':
        // Redact id
        sanitized[key] = '[REDACTED_ID]';
        break;

      case 'profileImage':
        // Redact profile image
        sanitized[key] = '[REDACTED_PROFILE_IMAGE]';
        break;

      default:
        // Sanitize other values
        sanitized[key] = sanitizeValue(value);
    }
  }

  return sanitized;
}

/**
 * Sanitizes an entire object recursively
 */
export function sanitizeObject(obj: unknown, depth = 0): unknown {
  // Prevent infinite recursion
  if (depth > 10) return '[MAX_DEPTH_REACHED]';

  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, depth + 1));
  }

  if (typeof obj === 'object') {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      // Check if the key suggests sensitive data
      const lowerKey = key.toLowerCase();

      if (lowerKey.includes('password') ||
          lowerKey.includes('secret') ||
          lowerKey.includes('token') ||
          lowerKey.includes('key') && lowerKey !== 'publickey') {
        sanitized[key] = '[REDACTED]';
      } else if ((lowerKey.includes('wallet') || lowerKey.includes('address')) && typeof value === 'string') {
        sanitized[key] = maskWalletAddress(value);
      } else {
        sanitized[key] = sanitizeObject(value, depth + 1);
      }
    }

    return sanitized;
  }

  return sanitizeValue(obj);
}
